import Box from '@material-ui/core/Box';
import CustomSnackBar from '../audienceManager/customSnackBar';
import DataTable from 'components/DataTable/DataTable';
import GhostLoader from 'react-ghost-loader';
import Header from 'components/Header';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TextFieldSearch from 'components/TextFieldSearch/TextFieldSearch';
import _debounce from 'lodash/debounce';
import moment from 'moment';
import { CaretDown } from '@phosphor-icons/react';
import {
  LoadingStates,
  OTS_SEGMENT_MANAGER_DEFAULT_PLATFORM,
  OTS_SEGMENT_MANAGER_DEFAULT_SORT_BY,
  OTS_SEGMENT_MANAGER_DEFAULT_SORT_ORDER,
} from '../../constants';
import {
  OAUTH_PLATFORMS_FILTER,
  OTS_SEGMENT_MANAGER,
  OTS_SGM_MNG_LAST_SYNCED,
  OTS_SGM_MNG_NO_SEGMENTS_FOUND,
  OTS_SGM_MNG_SEARCH_SEGMENT_NAME,
} from '../../content';
import { PLATFORM_OPTIONS } from './constants';
import {
  ThemeProvider,
  createStyles,
  createTheme,
  makeStyles,
} from '@material-ui/core/styles';
import {
  fetchSegments,
  removeNotification,
  updateFilters,
  updateSearchAndResetPage,
} from './slice';
import { getColumnsByPlatform } from './utils/utils';
import { renderRowCellsByPlatform } from './utils/renderers';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import * as Styled from './index.styles';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
    },
  })
);

const inputs = createTheme({
  overrides: {
    MuiFormControl: {
      root: {
        maxHeight: 48,
      },
    },
    MuiInputLabel: {
      root: {
        marginTop: -5,
        '&$focused': {
          marginTop: 0,
        },
      },
    },
    MuiInputBase: {
      root: {
        maxHeight: 48,
      },
      input: {
        borderRadius: 10,
        alignSelf: 'center',
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 10,
      },
      input: {
        padding: '12px 14px',
      },
    },
  },
});

const OtsSegmentManager: React.FC = () => {
  const classes = useStyles();
  // Initialize hooks first
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState(
    OTS_SEGMENT_MANAGER_DEFAULT_PLATFORM
  );
  const [sortBy, setSortBy] = useState<string>(
    OTS_SEGMENT_MANAGER_DEFAULT_SORT_BY
  );
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(
    OTS_SEGMENT_MANAGER_DEFAULT_SORT_ORDER
  );

  // Get state after hooks are initialized
  const otsSegmentManagerState = useAppSelector(
    (state) => state.otsSegmentManagerReducer
  );

  // Get debouncedSearchText from Redux
  const debouncedSearchText =
    otsSegmentManagerState?.ui?.filters?.debouncedSearchText || '';

  // Defensive check for state
  const entities = otsSegmentManagerState?.entities || {
    segments: [],
    lastSyncAt: undefined,
    totalRecords: 0,
  };
  const ui = otsSegmentManagerState?.ui || {
    loading: { state: LoadingStates.IDLE },
    filters: { page: 1, recordPerPage: 25 },
    notifications: [],
  };

  const loading = ui?.loading?.state === LoadingStates.LOADING;
  const error = ui?.loading?.state === LoadingStates.ERROR;

  // Format lastSyncAt for display
  const formattedDate = entities.lastSyncAt
    ? moment(entities.lastSyncAt).format('MM/DD/YYYY')
    : '';

  const formattedTime = entities.lastSyncAt
    ? moment(entities.lastSyncAt).format('hh:mm A')
    : '';

  useEffect(() => {
    const debounced = _debounce((value: string) => {
      dispatch(updateSearchAndResetPage(value));
    }, 1000);

    debounced(searchText);

    return () => {
      debounced.cancel();
    };
  }, [searchText, dispatch]);

  useEffect(() => {
    if (!ui?.filters) return;

    dispatch(
      fetchSegments({
        sortBy: sortBy,
        sortOrder: sortOrder,
        search: debouncedSearchText || undefined,
        platform: selectedPlatform || undefined,
        page: ui.filters.page || 1,
        recordPerPage: ui.filters.recordPerPage || 25,
      })
    );
  }, [
    dispatch,
    sortBy,
    sortOrder,
    debouncedSearchText,
    selectedPlatform,
    ui?.filters?.page,
    ui?.filters?.recordPerPage,
  ]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchText(event.target.value);
    },
    []
  );

  const handlePlatformChange = useCallback(
    (value: string) => {
      setSelectedPlatform(value);
      dispatch(updateFilters({ page: 1 }));
    },
    [dispatch]
  );

  const handleSortChange = useCallback(
    ({ column }: { column: string }) => {
      if (sortBy === column) {
        setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
      } else {
        setSortBy(column);
        setSortOrder('ASC');
      }
      dispatch(updateFilters({ page: 1 }));
    },
    [sortBy, sortOrder, dispatch]
  );

  const handlePaginationClick = (newPage: number) => {
    dispatch(updateFilters({ page: newPage + 1 }));
  };

  const handleRowsPerPageChange = useCallback(
    (newRowsPerPage: number) => {
      dispatch(updateFilters({ recordPerPage: newRowsPerPage, page: 1 }));
    },
    [dispatch]
  );

  const columns = useMemo(
    () => getColumnsByPlatform(selectedPlatform, sortBy, sortOrder),
    [selectedPlatform, sortBy, sortOrder]
  );

  const rows = useMemo(() => {
    return entities?.segments?.map((segment) =>
      renderRowCellsByPlatform(selectedPlatform, segment)
    );
  }, [entities.segments, selectedPlatform]);

  return (
    <>
      <Box className={classes.root}>
        <Header
          title={OTS_SEGMENT_MANAGER}
          style={{ padding: '20px 32px' }}
          action={
            loading ? (
              <Styled.LastSyncedContainer>
                <Styled.LastSyncedLabel>
                  {OTS_SGM_MNG_LAST_SYNCED}
                </Styled.LastSyncedLabel>
                <GhostLoader height="16px" width="100px" />
                <GhostLoader height="16px" width="80px" />
              </Styled.LastSyncedContainer>
            ) : entities.lastSyncAt ? (
              <Styled.LastSyncedContainer>
                <Styled.LastSyncedLabel>
                  {OTS_SGM_MNG_LAST_SYNCED}
                </Styled.LastSyncedLabel>
                <Styled.LastSyncedDate>{formattedDate}</Styled.LastSyncedDate>
                <Styled.LastSyncedTime>{formattedTime}</Styled.LastSyncedTime>
              </Styled.LastSyncedContainer>
            ) : null
          }
        />

        <Box height="100%">
          <Styled.SearchArea>
            <ThemeProvider theme={inputs}>
              <Styled.SearchTextField>
                <TextFieldSearch
                  placeholder={OTS_SGM_MNG_SEARCH_SEGMENT_NAME}
                  value={searchText}
                  onChange={handleSearchChange}
                  disabled={loading}
                  style={{
                    width: '100%',
                  }}
                />
              </Styled.SearchTextField>
            </ThemeProvider>
            <Styled.SelectAutoCompletePlatform
              disablePortal
              id="select-segment-platform"
              options={PLATFORM_OPTIONS}
              popupIcon={<CaretDown />}
              disabled={loading}
              value={
                PLATFORM_OPTIONS.find(
                  (opt) => opt.value === selectedPlatform
                ) || null
              }
              onChange={(_, value: any) => {
                if (value?.value !== undefined) {
                  handlePlatformChange(value.value || '');
                }
              }}
              getOptionLabel={(option: any) => option.label}
              renderInput={(params) => (
                <TextFieldSearch
                  {...params}
                  placeholder={OAUTH_PLATFORMS_FILTER}
                  variant="outlined"
                  disabled={loading}
                />
              )}
              renderOption={(props, option: any) => (
                <Styled.SelectAutoCompleteGroupOptions>
                  <li {...props} key={option.value || option.label}>
                    {option.label}
                  </li>
                </Styled.SelectAutoCompleteGroupOptions>
              )}
            />
          </Styled.SearchArea>
          <div style={{ padding: '24px 32px', background: '#F0EFF1' }}>
            <DataTable
              loading={loading}
              error={error}
              columns={columns}
              rows={(rows as any) || []}
              customRowsPerPageOptions={[25, 50, 100]}
              totalRows={entities?.totalRecords || 0}
              defaultRowsPerPage={ui?.filters?.recordPerPage || 25}
              page={ui?.filters?.page ? ui.filters.page - 1 : 0}
              onSortChange={handleSortChange}
              onPaginationClick={handlePaginationClick}
              onRowsPerPageChange={handleRowsPerPageChange}
              showRowsPerPageDropdown={true}
              noDataMessage={OTS_SGM_MNG_NO_SEGMENTS_FOUND}
            />
          </div>
        </Box>
      </Box>
      {ui.notifications.map(({ state, message }, i) => (
        <CustomSnackBar
          index={i}
          trigger={true}
          severity={
            state === 'done'
              ? 'success'
              : state === 'warning'
              ? 'warning'
              : 'error'
          }
          message={message || ''}
          key={i}
          removeNotificationFunction={() => dispatch(removeNotification(i))}
        />
      ))}
    </>
  );
};

export default OtsSegmentManager;
